import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
  ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Box } from '@material-ui/core';
import { alpha, makeStyles, useTheme } from '@material-ui/core/styles';
import { useApi, configApiRef, identityApiRef, ConfigApi } from '@backstage/core-plugin-api';
import { useOptionalEntity } from './useOptionalEntity';
import { Entity } from '@backstage/catalog-model';
import { resolveNamespace } from './environments';
import { useHeaderRightSlot } from './HeaderSlot';
import { DEFAULT_ENV_COLOR, envTextColor, manewcoTokens, useManewcoTokens } from './manewcoTokens';
export interface EnvConfig {
  key: string;
  displayName: string;
  namespace: string;
  color: string;
  branch: string;
  /** Environnement en lecture seule (absent ou invalide dans la config => true). */
  protected: boolean;
}

interface EnvironmentContextValue {
  /** Environnements déclarés dans `platform.environments`. */
  envs: EnvConfig[];
  /** Environnement sélectionné (undefined si aucun n'est configuré). */
  activeEnv: EnvConfig | undefined;
  /** Index de `activeEnv` dans `envs`, ou 0 par défaut. */
  activeIndex: number;
  /**
   * « Tous » est sélectionné. Possible seulement sur une page qui l'accepte
   * (`useEnvironmentOrAll`) ; ailleurs, toujours `false`.
   */
  allSelected: boolean;
  /**
   * Sélectionne un environnement par sa clé (`prod`, `preprod`…), ou « Tous »
   * par `ALL_ENVIRONMENTS`.
   */
  setActiveEnv: (key: string) => void;
}

const EnvironmentContext = createContext<EnvironmentContextValue | undefined>(
  undefined,
);

/** Paramètre d'URL portant l'environnement courant (`?env=prod`). */
const ENV_SEARCH_PARAM = 'env';

/**
 * Clé du miroir de session.
 *
 * L'URL reste la source de vérité (un lien partagé doit pointer sans ambiguïté
 * sur le bon environnement), mais elle ne survit pas à la navigation : les
 * onglets d'entité sont rendus par `RoutedTabs`, dont les liens portent un
 * chemin relatif *sans* query string — changer d'onglet effaçait donc `?env=`
 * et ramenait tout le monde sur le premier environnement déclaré. Le miroir
 * rejoue le dernier choix quand l'URL n'en porte pas, ce qui fait suivre
 * l'environnement sur toutes les pages, pas seulement entre onglets.
 */
const ENV_STORAGE_KEY = 'platform.activeEnv';

export function readStoredEnv(): string | null {
  try {
    return window.sessionStorage.getItem(ENV_STORAGE_KEY);
  } catch {
    // Stockage indisponible (navigation privée, cookies bloqués) : on se
    // contente de l'URL plutôt que de faire tomber la page d'entité.
    return null;
  }
}

export function storeEnv(key: string): void {
  try {
    window.sessionStorage.setItem(ENV_STORAGE_KEY, key);
  } catch {
    /* cf. readStoredEnv */
  }
}

/**
 * Valeur de `?env=` pour « Tous ».
 *
 * « Tous » n'a de sens que sur les pages qui l'acceptent — les pages IA, dont
 * le filtre porte sur un champ libre de Langfuse où « voir tout » reste utile.
 * Sur une page d'entité, un namespace Kubernetes est toujours précis : il n'y a
 * pas de « Tous ».
 */
export const ALL_ENVIRONMENTS = 'all';

/**
 * « Tous » a sa propre mémoire, distincte de `platform.activeEnv` : choisir
 * « Tous » sur une page IA ne doit pas faire oublier aux pages d'entité le
 * dernier environnement réel choisi. Et il doit survivre au passage d'une page
 * IA à l'autre, dont les liens du menu ne portent pas `?env=`.
 */
const ALL_STORAGE_KEY = 'platform.allEnvs';

function readStoredAll(): boolean {
  try {
    return window.sessionStorage.getItem(ALL_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function storeAll(all: boolean): void {
  try {
    if (all) window.sessionStorage.setItem(ALL_STORAGE_KEY, 'true');
    else window.sessionStorage.removeItem(ALL_STORAGE_KEY);
  } catch {
    /* cf. readStoredEnv */
  }
}

/**
 * Nombre de composants montés qui acceptent « Tous ».
 *
 * Le sélecteur est monté à la racine de l'application, la page en dessous de
 * lui : elle ne peut pas lui passer de prop ni de contexte. Elle se déclare donc
 * ici à son montage (`useEnvironmentOrAll`), et le sélecteur s'y abonne. Un
 * compteur plutôt qu'un booléen : deux composants de la même page peuvent se
 * déclarer, et le démontage de l'un ne doit pas retirer « Tous » à l'autre.
 */
let allAcceptors = 0;
const allListeners = new Set<() => void>();

function subscribeAll(listener: () => void) {
  allListeners.add(listener);
  return () => {
    allListeners.delete(listener);
  };
}

function setAllAcceptors(delta: number) {
  allAcceptors += delta;
  allListeners.forEach(listener => listener());
}

function useAllAccepted(): boolean {
  return useSyncExternalStore(subscribeAll, () => allAcceptors > 0);
}

/**
 * Sélection effective, commune au fournisseur et à `useEnvironmentOrAll` pour
 * qu'ils ne divergent jamais.
 *
 * Priorité : l'URL, puis la mémoire de session. Sur une page qui accepte
 * « Tous », c'est aussi le défaut quand rien n'est choisi, ou quand la clé est
 * inconnue : le champ Langfuse est libre, et démarrer sur un environnement
 * précis masquait toute donnée étiquetée autrement. Ailleurs, `?env=all` est
 * ignoré et une clé inconnue retombe sur le premier environnement déclaré.
 */
function resolveSelection(
  envKeys: string[],
  requestedKey: string | null,
  allAccepted: boolean,
): { all: boolean; index: number } {
  const explicitKey =
    requestedKey !== null && requestedKey !== ALL_ENVIRONMENTS
      ? requestedKey
      : null;
  // `?env=all` sur une page sans « Tous » compte comme une URL muette : on
  // reprend le dernier environnement réel plutôt que le premier déclaré.
  const silent = requestedKey === null || requestedKey === ALL_ENVIRONMENTS;
  const effectiveKey = explicitKey ?? (silent ? readStoredEnv() : null);
  const foundIndex = envKeys.findIndex(k => k === effectiveKey);

  if (allAccepted) {
    const all =
      requestedKey === ALL_ENVIRONMENTS ||
      (requestedKey === null && (readStoredAll() || effectiveKey === null)) ||
      foundIndex < 0;
    if (all) return { all: true, index: 0 };
  }
  return { all: false, index: foundIndex >= 0 ? foundIndex : 0 };
}

/** Seul un vrai booléen `false` déverrouille ; tout le reste est protégé. */
function readProtected(value: unknown): boolean {
  return value === false ? false : true;
}

/**
 * `entity` est requis pour honorer l'annotation `platform.io/namespace-template`
 * (cf. `resolveNamespace`) : un composant peut vivre dans ses propres namespaces
 * (`espace-formalite-agent-prod`…) plutôt que dans ceux de l'app principale.
 */
function readEnvs(
  configApi: ConfigApi,
  entity: Entity | undefined,
): EnvConfig[] {
  try {
    const arr = configApi.getConfigArray('platform.environments');
    return arr.map(c => {
      const key = c.getString('key');
      const configured = c.getOptionalString('namespace');
      return {
        key,
        displayName: c.getString('displayName'),
        namespace: entity
          ? resolveNamespace(entity, key, configured)
          : configured ?? 'default',
        color: c.getOptionalString('color') ?? DEFAULT_ENV_COLOR,
        branch: c.getOptionalString('branch') ?? 'main',
        protected: readProtected(c.getOptional('protected')),
      };
    });
  } catch {
    return [];
  }
}

/**
 * Fournit l'environnement courant à toute la page d'entité.
 *
 * La sélection vit dans l'URL (`?env=prod`) et non dans un état local : un lien
 * partagé pointe donc sans ambiguïté sur le bon environnement, et le choix
 * survit au rechargement comme au changement d'onglet.
 */
export const EnvironmentProvider = ({ children }: { children: ReactNode }) => {
  const configApi = useApi(configApiRef);
  const [searchParams, setSearchParams] = useSearchParams();
  // `useOptionalEntity` plutôt que `useEntity` ou `useAsyncEntity` : le premier
  // lève tant que l'entité n'est pas résolue, le second lève hors d'une page
  // d'entité. Or ce fournisseur est monté dans la coquille de l'application
  // (phase 4b) pour que le sélecteur d'environnement survive au passage au
  // catalogue natif, qui jette l'enveloppe de l'ancienne page d'entité.
  const entity = useOptionalEntity();

  const envs = useMemo(() => readEnvs(configApi, entity), [configApi, entity]);

  const allAccepted = useAllAccepted();

  // Une clé explicite dans l'URL prime toujours : un lien partagé avec
  // `?env=prod` ne doit pas être détourné par le dernier choix local. Une clé
  // absente ou inconnue retombe sur le premier environnement déclaré (par
  // convention le moins critique, cf. `platform.environments`) — ou sur « Tous »
  // là où il est accepté, voir `resolveSelection`.
  const requestedKey = searchParams.get(ENV_SEARCH_PARAM);
  const { all: allSelected, index: activeIndex } = resolveSelection(
    envs.map(e => e.key),
    requestedKey,
    allAccepted,
  );
  const activeEnv = envs[activeIndex];

  // Recolle le choix mémorisé dans l'URL dès qu'une navigation l'a perdu, pour
  // que l'adresse reste partageable et que les composants qui lisent `?env=`
  // directement voient la même chose que le contexte.
  const storedKey = requestedKey === null ? readStoredEnv() : null;
  const storedAll = requestedKey === null && allSelected && readStoredAll();
  const knownStoredKey = envs.some(e => e.key === storedKey) ? storedKey : null;
  // « Tous » mémorisé mais pas (encore) accepté : on ne recolle rien. La page IA
  // est chargée paresseusement et ne se déclare qu'après ce premier passage ;
  // recoller l'environnement réel ici écraserait « Tous » dans l'URL avant
  // qu'elle ait pu le lire. Une fois déclarée, cet effet repasse et recolle `all`.
  // Sur une page d'entité, l'URL reste simplement muette — la sélection, elle,
  // vient de la mémoire.
  const allPending = requestedKey === null && !allAccepted && readStoredAll();
  const restoredKey = storedAll
    ? ALL_ENVIRONMENTS
    : allPending
    ? null
    : knownStoredKey;
  useEffect(() => {
    if (requestedKey !== null || !restoredKey) return;
    const next = new URLSearchParams(searchParams);
    next.set(ENV_SEARCH_PARAM, restoredKey);
    setSearchParams(next, { replace: true });
  }, [requestedKey, restoredKey, searchParams, setSearchParams]);

  const setActiveEnv = useCallback(
    (key: string) => {
      // « Tous » ne remplace pas la mémoire de l'environnement réel : voir
      // `ALL_STORAGE_KEY`.
      if (key === ALL_ENVIRONMENTS) {
        storeAll(true);
      } else {
        storeAll(false);
        storeEnv(key);
      }
      const next = new URLSearchParams(searchParams);
      next.set(ENV_SEARCH_PARAM, key);
      // `replace` : basculer d'environnement ne doit pas empiler des entrées
      // d'historique que l'utilisateur devrait dépiler une à une.
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const value = useMemo(
    () => ({ envs, activeEnv, activeIndex, allSelected, setActiveEnv }),
    [envs, activeEnv, activeIndex, allSelected, setActiveEnv],
  );

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};

/**
 * Accède à l'environnement courant.
 *
 * Hors d'un `EnvironmentProvider`, renvoie un contexte vide plutôt que de
 * lever : les cartes concernées sont aussi montées dans des pages d'entité qui
 * n'ont pas (encore) de sélecteur global.
 */
export function useEnvironment(): EnvironmentContextValue {
  const ctx = useContext(EnvironmentContext);
  const configApi = useApi(configApiRef);
  const entity = useOptionalEntity();
  // Lu même quand le contexte existe : un hook ne s'appelle pas sous condition.
  const [searchParams] = useSearchParams();
  const fallbackEnvs = useMemo(
    () => readEnvs(configApi, entity),
    [configApi, entity],
  );

  if (ctx) return ctx;

  // Hors provider — c'est le cas des onglets d'entité, le fournisseur
  // n'enveloppant que le sélecteur. L'URL d'abord : lue via `useSearchParams`,
  // elle re-rend l'onglet quand le sélecteur la change. Lire la seule mémoire
  // de session figeait l'onglet sur l'environnement du premier rendu.
  const { index: fallbackIndex } = resolveSelection(
    fallbackEnvs.map(e => e.key),
    searchParams.get(ENV_SEARCH_PARAM),
    false,
  );
  return {
    envs: fallbackEnvs,
    activeEnv: fallbackEnvs[fallbackIndex],
    activeIndex: fallbackIndex,
    allSelected: false,
    setActiveEnv: () => {},
  };
}

/**
 * Environnement choisi dans le sélecteur global, pour une page qui accepte
 * « Tous » : renvoie la clé (`prod`, `preprod`…), ou `''` pour « Tous ».
 *
 * L'appeler suffit à faire apparaître « Tous » dans le sélecteur, le temps que
 * le composant est monté. Lit l'URL elle-même plutôt que le contexte : le
 * fournisseur n'enveloppe que le sélecteur, monté à la racine, pas les pages.
 */
export function useEnvironmentOrAll(): string {
  const configApi = useApi(configApiRef);
  const [searchParams] = useSearchParams();
  const envKeys = useMemo(
    () => readEnvs(configApi, undefined).map(e => e.key),
    [configApi],
  );

  useEffect(() => {
    setAllAcceptors(1);
    return () => setAllAcceptors(-1);
  }, []);

  // `true` d'emblée, sans attendre l'effet ci-dessus : le premier rendu de la
  // page doit déjà lire « Tous » plutôt qu'un environnement, sans quoi elle
  // lancerait une première requête filtrée aussitôt remplacée.
  const { all, index } = resolveSelection(
    envKeys,
    searchParams.get(ENV_SEARCH_PARAM),
    true,
  );
  return all ? '' : envKeys[index] ?? '';
}

const useStyles = makeStyles(theme => ({
  // Repli : rendu en place quand la page n'a pas de bandeau d'en-tête où
  // se projeter (cf. useHeaderRightSlot).
  fallbackBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1, 0, 1.5),
    marginBottom: theme.spacing(1),
  },
  // Maquette « Clarté complète » : « Environnement » + liste déroulante (.env-select).
  group: {
    // order: -1 impose le sélecteur en premier, à gauche du bandeau (portails ajoutés en fin
    // de conteneur dans l'ordre de montage). N'affecte pas le repli (un seul enfant).
    order: -1,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  label: { fontSize: 11, color: theme.palette.text.secondary },
  // Production : impossible à manquer (sélecteur rouge, avertissement, trait en haut de l'écran).
  production: { fontWeight: 700, color: manewcoTokens(theme).production.text },
  warning: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  stripe: { position: 'fixed', top: 0, left: 0, right: 0, height: 4, zIndex: 2000, pointerEvents: 'none' },
  select: {
    fontFamily: theme.typography.fontFamily,
    fontSize: 11,
    padding: '8px 28px 8px 10px',
    minHeight: 34,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    cursor: 'pointer',
    '&:focus-visible': { outline: `3px solid ${theme.palette.secondary.main}`, outlineOffset: 2 },
  },
}));

/**
 * Sélecteur d'environnement global, affiché dans l'en-tête ou en repli.
 *
 * Rendu en contrôle segmenté (et non en onglets) parce qu'il change le *scope*
 * de toute la page, alors que les onglets en dessous naviguent entre sections.
 * L'environnement actif est rempli de sa couleur (`platform.environments[].color`)
 * pour rester lisible en permanence — on édite des secrets sur ces écrans.
 */
/** Vrai une fois l'utilisateur connecté (l'identité Backstage reste en attente avant). */
const useSignedIn = () => {
  const identityApi = useApi(identityApiRef);
  const [signedIn, setSignedIn] = useState(false);
  useEffect(() => {
    let cancelled = false;
    identityApi
      .getBackstageIdentity()
      .then(() => !cancelled && setSignedIn(true))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [identityApi]);
  return signedIn;
};

export const GlobalEnvSwitcher = () => {
  const classes = useStyles();
  const theme = useTheme();
  const tokens = useManewcoTokens();
  const { envs, activeIndex, allSelected, setActiveEnv } = useEnvironment();
  const allAccepted = useAllAccepted();
  const slot = useHeaderRightSlot();
  const signedIn = useSignedIn();

  // Avant la connexion (page de connexion), pas de sélecteur : l'identité reste en attente.
  if (!signedIn || envs.length < 2) return null;

  const id = 'platform-environment-select';
  const current = allSelected ? undefined : envs[activeIndex];
  // La production (clé « prod ») est signalée partout : on ne doit pas y agir par mégarde.
  const isProduction = current?.key === 'prod';
  const prodColor = current?.color ?? tokens.production.background;
  // En sombre, la teinte foncée de la production ne ressort plus sur le fond : l'avertissement
  // et le trait du haut prennent sa version éclaircie ; le sélecteur garde le rouge plein.
  const prodText = envTextColor(theme, prodColor);
  const group = (
    <Box className={classes.group}>
      <label htmlFor={id} className={classes.label}>
        Environnement
      </label>
      <select
        id={id}
        className={`${classes.select}${isProduction ? ` ${classes.production}` : ''}`}
        data-production={String(isProduction)}
        style={isProduction ? { backgroundColor: prodColor, borderColor: prodColor, color: tokens.production.text, fontWeight: 700 } : undefined}
        value={allSelected ? ALL_ENVIRONMENTS : envs[activeIndex]?.key}
        onChange={e => setActiveEnv(e.target.value)}
      >
        {allAccepted && <option value={ALL_ENVIRONMENTS}>Tous</option>}
        {envs.map(env => (
          <option key={env.key} value={env.key}>
            {env.displayName}
          </option>
        ))}
      </select>
      {isProduction && (
        <span role="alert" className={classes.warning} style={{ color: prodText, backgroundColor: alpha(prodText, 0x14 / 255), boxShadow: `inset 0 0 0 1px ${alpha(prodText, 0x55 / 255)}` }}>
          ⚠ Production : actions réelles
        </span>
      )}
    </Box>
  );
  const stripe =
    isProduction && typeof document !== 'undefined'
      ? createPortal(
          <div data-testid="production-stripe" aria-hidden className={classes.stripe} style={{ backgroundColor: prodText }} />,
          document.body,
        )
      : null;

  // Projeté dans l'en-tête quand la page en a un, sinon rendu en place :
  // le choix d'environnement ne doit jamais devenir inatteignable.
  return (
    <>
      {stripe}
      {slot ? createPortal(group, slot) : <Box className={classes.fallbackBar}>{group}</Box>}
    </>
  );
};
